import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useChatbotStore } from '@/stores/chatbot'
import { isConversationPage, useChatPanelStore } from '@/stores/chatPanel'

/** How much of the person's own request names the work in progress. */
const SUBJECT_MAX = 48

/** The last thing the person asked, shortened — what «J'y travaille» is about. */
export function workSubject(text: string): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length > SUBJECT_MAX ? `${flat.slice(0, SUBJECT_MAX - 1).trimEnd()}…` : flat
}

/**
 * Redesign — is the assistant working, and has a reply landed that the person
 * has not seen? Installed ONCE by the app shell (HomeView); every surface
 * (the dock's edge tab, its «C'est prêt» line, the project rail) reads the
 * state from the chat panel store.
 *
 * "Seen" means the conversation was in view when the reply finished, or has
 * been since: the dock open, a chat page, or the Pilotage conversation.
 */
export function useChatActivityWatch(): void {
  const chatbot = useChatbotStore()
  const panel = useChatPanelStore()
  const route = useRoute()

  const working = computed(() => chatbot.isLoading || chatbot.isStreaming)
  const conversationInView = computed(() => {
    if (panel.isVisible) return true
    return isConversationPage(route)
  })

  // A TURN, not any busy spell: the store is also busy while it creates or
  // resets a session, and a turn can end on an error. Only a turn the person
  // sent counts as work, and only an answer that actually arrived counts as
  // «C'est prêt» (review of PR 852: errors and resets raised a false «ready»).
  //
  // The turn is recognised whenever the person's message shows up during the
  // busy spell, not only at its first instant: `sendMessage` turns busy BEFORE
  // it awaits the service address and the token and pushes the user's message
  // (review of PR 884: read at the flip, the sign never fired on a real send).
  let messagesAtStart = -1
  watch(
    () => [working.value, chatbot.messages?.length ?? 0] as const,
    ([busy]) => {
      const messages = chatbot.messages ?? []
      const last = messages[messages.length - 1]
      // `!==`, not `< 0`: after a turn that ended on an error the old question is
      // still last when the chat turns busy again; the new one replaces it as the
      // subject once it lands (review of PR 884).
      if (busy && last?.role === 'user' && messages.length !== messagesAtStart) {
        messagesAtStart = messages.length
        panel.markWorking(workSubject(last.parts.map(p => p.text ?? '').join('')))
      } else if (!busy && messagesAtStart >= 0) {
        const answered = !chatbot.error && messages.length > messagesAtStart && last?.role === 'model'
        messagesAtStart = -1
        panel.markDone(answered && !conversationInView.value)
      }
    },
  )

  watch(conversationInView, (inView) => { if (inView) panel.markSeen() }, { immediate: true })
}
